/** Admin Settings → Onboarding: edit the step list + see every person's progress (tick off "office" steps). */
import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Check, Loader2, ChevronDown, ChevronUp, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const openFile = (path) => api.get(path, { responseType: "blob" }).then(({ data }) => window.open(URL.createObjectURL(data), "_blank")).catch(() => toast.error("Could not open the file"));

const KINDS = { upload: "Upload a document", ack: "Tick to acknowledge", info: "Office does this" };

export default function OnboardingSettings() {
  const [steps, setSteps] = useState(null);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [openUser, setOpenUser] = useState("");
  const load = () => api.get("/onboarding/admin").then(({ data }) => { setSteps(data.steps); setUsers(data.users); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!steps) return null;

  const upd = (i, patch) => setSteps(steps.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/onboarding/settings", { steps }); setSteps(data.steps); toast.success("Onboarding steps saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  const toggle = async (u, s, done) => {
    try {
      if (done) await api.delete(`/onboarding/admin/${u.user_id}/${s.id}`); else await api.post(`/onboarding/admin/${u.user_id}/${s.id}`, {});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  return (
    <div className="space-y-4" data-testid="onboarding-settings">
      <div className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-xs text-white/70">
        <div className="flex items-center gap-2">
          <p className="flex-1 text-sm font-semibold text-white">Onboarding steps</p>
          <button onClick={() => setSteps([...steps, { id: "", title: "", detail: "", kind: "ack", required: true }])} className="inline-flex items-center gap-1 rounded-md bg-white/10 px-2.5 py-1 font-semibold text-white hover:bg-white/15" data-testid="onboarding-add-step"><Plus className="h-3.5 w-3.5" /> Add step</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1 rounded-md bg-[#1a3fd1] px-2.5 py-1 font-semibold text-white disabled:opacity-50" data-testid="onboarding-save-steps">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save</button>
        </div>
        <p className="mt-1 text-white/40">Every new hire sees this list on their Home tab until all required steps are done.</p>
        <div className="mt-3 space-y-2">
          {steps.map((s, i) => (
            <div key={i} className="grid grid-cols-1 gap-2 rounded-md border border-white/10 bg-black/20 p-2 sm:grid-cols-[1fr_1fr_10rem_auto_auto]" data-testid={`onboarding-step-row-${i}`}>
              <input value={s.title} onChange={(e) => upd(i, { title: e.target.value })} placeholder="Step title" className="rounded-md border border-white/15 bg-white/5 px-2 py-1 text-white" />
              <input value={s.detail} onChange={(e) => upd(i, { detail: e.target.value })} placeholder="What they need to do (optional)" className="rounded-md border border-white/15 bg-white/5 px-2 py-1 text-white" />
              <select value={s.kind} onChange={(e) => upd(i, { kind: e.target.value })} className="rounded-md border border-white/15 bg-[#111] px-2 py-1 text-white">
                {Object.entries(KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <label className="inline-flex items-center gap-1 whitespace-nowrap"><input type="checkbox" checked={s.required} onChange={(e) => upd(i, { required: e.target.checked })} className="accent-[#1a3fd1]" /> required</label>
              <button onClick={() => setSteps(steps.filter((_, j) => j !== i))} className="rounded-md bg-white/10 p-1.5 text-red-300 hover:bg-white/15" aria-label="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-xs text-white/70">
        <p className="text-sm font-semibold text-white">Who's done what</p>
        <ul className="mt-2 divide-y divide-white/5">
          {users.map((u) => (
            <li key={u.user_id} data-testid={`onboarding-user-${u.user_id}`}>
              <button onClick={() => setOpenUser(openUser === u.user_id ? "" : u.user_id)} className="flex w-full items-center gap-2 py-2 text-left">
                <span className={`h-2 w-2 rounded-full ${u.complete ? "bg-emerald-400" : "bg-amber-400"}`} />
                <span className="flex-1 font-semibold text-white">{u.name || u.email}</span>
                <span className="text-white/50">{u.done}/{u.required} required{u.complete ? " · complete" : ""}</span>
                {openUser === u.user_id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
              {openUser === u.user_id && (
                <ul className="mb-2 space-y-1 pl-4">
                  {steps.filter((s) => s.id).map((s) => {
                    const rec = u.items?.[s.id];
                    return (
                      <li key={s.id} className="flex items-center gap-2">
                        <button onClick={() => toggle(u, s, !!rec)} className={`flex h-4 w-4 items-center justify-center rounded border ${rec ? "border-emerald-400 bg-emerald-500 text-white" : "border-white/30"}`} data-testid={`onboarding-admin-toggle-${u.user_id}-${s.id}`}>{rec && <Check className="h-3 w-3" />}</button>
                        <span className={rec ? "text-white/50 line-through" : "text-white/80"}>{s.title}</span>
                        {rec?.file_name && <button onClick={() => openFile(`/onboarding/file/${u.user_id}/${s.id}`)} className="inline-flex items-center gap-1 text-sky-300 underline"><FileText className="h-3 w-3" /> {rec.file_name}</button>}
                        {rec && <span className="text-white/30">{new Date(rec.at).toLocaleDateString()}</span>}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
